import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from 'context/AuthContext';
import {
  getLimiteVotosEtapa3,
  isAssessorEtapa3,
  isVpEtapa3,
  getVpDestino,
  type StatusVotoPreliminar,
} from 'types/etapa3';
import {
  fetchRascunhoAtivo,
  fetchTodasSelecoesByUser,
  salvarRascunho,
  deletarRascunhoAtivo,
  submitSelecaoEtapa3,
} from 'services/selecao-etapa3.service';
import {
  fetchUltimoPreliminarByAssessor,
  fetchPreliminarEmAprovacaoByVp,
  salvarPreliminarRascunho,
  enviarPreliminarParaAprovacao,
  marcarPreliminarSuperseded,
} from 'services/voto-preliminar-etapa3.service';
import { registrarVotar } from 'services/iniciativa-action.service';
import {
  CATEGORIAS_PARTICIPANTES_ETAPA3,
  isPreliminarElbb,
} from 'pages/HomeElbb/components/CategoriasParticipantesEtapa3';

export type VotosMap = Record<string, { ids: string[]; titulos: string[] }>;
export type PerfilEtapa3 = 'assessor' | 'vp' | 'comum';

const PRELIMINAR_DISMISS_KEY = 'jj_etapa3_preliminar_dismissed';

export function useVotacaoEtapa3() {
  const { user } = useAuth();
  const navigate = useNavigate();

  const perfil: PerfilEtapa3 = useMemo(
    () =>
      isAssessorEtapa3(user ?? null)
        ? 'assessor'
        : isVpEtapa3(user ?? null)
        ? 'vp'
        : 'comum',
    [user]
  );
  const vpDestino = useMemo(() => getVpDestino(user ?? null), [user]);
  const limite = useMemo(() => getLimiteVotosEtapa3(user ?? null), [user]);

  const [votos, setVotos] = useState<VotosMap>({});
  const [isLoadingRascunho, setIsLoadingRascunho] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState<string | null>(null);

  // Estado do assessor
  const [statusPreliminar, setStatusPreliminar] =
    useState<StatusVotoPreliminar | null>(null);
  const [preliminarSuperseded, setPreliminarSuperseded] = useState(false);
  // true quando o board foi semeado a partir do rascunho atual do VP
  const [preliminarBaseadoNoVp, setPreliminarBaseadoNoVp] = useState(false);
  // Estado do VP
  const [preliminarVp, setPreliminarVp] = useState<{ assessorNome: string } | null>(
    null
  );
  const [mostrarModalPreliminar, setMostrarModalPreliminar] = useState(false);

  const isReadOnly =
    perfil === 'assessor' &&
    (statusPreliminar === 'em_aprovacao' || preliminarSuperseded);

  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const votosRef = useRef<VotosMap>(votos);
  const autoSubmitTimersRef = useRef<Record<string, ReturnType<typeof setTimeout>>>(
    {}
  );
  const lastAutoSubmitSnapshotRef = useRef<VotosMap>({});
  const autoSubmitInitializedRef = useRef(false);
  // Notas do preliminar do VP, retidas enquanto o modal de escolha está aberto.
  const preliminarVpNotasRef = useRef<VotosMap | null>(null);

  useEffect(() => {
    votosRef.current = votos;
  }, [votos]);

  // ── Mount: carrega o estado conforme o perfil ───────────────────────────────
  useEffect(() => {
    if (!user?._id) return;
    let cancelled = false;

    (async () => {
      // Perfil assessor: lê o voto preliminar próprio.
      if (perfil === 'assessor') {
        const preliminarRaw = await fetchUltimoPreliminarByAssessor(user._id);
        if (cancelled) return;
        // voto_preliminar_etapa3__c é compartilhado com a Conexão — só considera
        // o preliminar se ele pertencer ao produto ELBB (categorias da Etapa 3).
        const preliminar = isPreliminarElbb(preliminarRaw?.notasRascunho)
          ? preliminarRaw
          : null;
        if (preliminar) {
          setVotos(preliminar.notasRascunho as VotosMap);
          if (preliminar.supersededAt) {
            setPreliminarSuperseded(true);
          } else {
            setStatusPreliminar(preliminar.status);
          }
        } else if (vpDestino?.matricula) {
          // Sem preliminar próprio: parte do rascunho atual do VP. É apenas
          // leitura sobre rascunho_etapa3__c — as edições do assessor são
          // gravadas em voto_preliminar_etapa3__c e não afetam o rascunho do VP.
          const rascunhoVp = await fetchRascunhoAtivo(vpDestino.matricula);
          if (cancelled) return;
          if (rascunhoVp?.notasRascunho) {
            setVotos(rascunhoVp.notasRascunho as VotosMap);
            setPreliminarBaseadoNoVp(true);
          }
        }
        setIsLoadingRascunho(false);
        return;
      }

      // Perfil vp ou comum: fluxo atual (rascunho → seleções enviadas).
      const rascunho = await fetchRascunhoAtivo(user._id);
      if (cancelled) return;

      let temEstadoProprio = false;
      if (rascunho?.notasRascunho) {
        setVotos(rascunho.notasRascunho as VotosMap);
        temEstadoProprio = true;
      } else {
        const selecoesTodas = await fetchTodasSelecoesByUser(user._id);
        if (cancelled) return;
        const idsElbb = new Set(CATEGORIAS_PARTICIPANTES_ETAPA3.map((c) => c.id));
        const selecoes = selecoesTodas.filter((s) => idsElbb.has(s.categoria));
        if (selecoes.length > 0) {
          const votosIniciais: VotosMap = {};
          selecoes.forEach((s) => {
            votosIniciais[s.categoria] = {
              ids: s.iniciativaIds,
              titulos: s.iniciativaTitulos,
            };
          });
          setVotos(votosIniciais);
          temEstadoProprio = true;
        }
      }

      // Perfil vp: além do estado próprio, verifica o preliminar do assessor.
      if (perfil === 'vp') {
        // Só considera preliminares do produto ELBB (collection compartilhada).
        const preliminar = await fetchPreliminarEmAprovacaoByVp(
          user._id,
          isPreliminarElbb
        );
        if (cancelled) return;
        if (preliminar) {
          const dismissed =
            sessionStorage.getItem(PRELIMINAR_DISMISS_KEY) === user._id;
          if (temEstadoProprio && !dismissed) {
            // Conflito: mantém o estado próprio e abre o modal de escolha.
            preliminarVpNotasRef.current = preliminar.notasRascunho as VotosMap;
            setPreliminarVp({ assessorNome: preliminar.assessorNome });
            setMostrarModalPreliminar(true);
          } else if (!temEstadoProprio) {
            // Sem conflito: carrega o preliminar direto e sinaliza o banner.
            setVotos(preliminar.notasRascunho as VotosMap);
            setPreliminarVp({ assessorNome: preliminar.assessorNome });
          }
        }
      }

      setIsLoadingRascunho(false);
    })();

    return () => {
      cancelled = true;
    };
  }, [user?._id, perfil, vpDestino?.matricula]);

  // ── Debounce de rascunho após mudança de votos ──────────────────────────────
  useEffect(() => {
    if (isLoadingRascunho || !user?._id) return;
    if (perfil === 'assessor' && isReadOnly) return; // travado pós-envio
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setIsSaving(true);
      try {
        if (perfil === 'assessor') {
          await salvarPreliminarRascunho(
            {
              assessorId: user._id,
              assessorNome: user.name,
              vpMatricula: vpDestino?.matricula ?? '',
              vpNome: vpDestino?.nome ?? '',
              notasRascunho: votosRef.current,
              status: 'rascunho',
              atualizadoEm: Date.now(),
            },
            isPreliminarElbb
          );
        } else {
          await salvarRascunho(user._id, votosRef.current);
        }
      } finally {
        setIsSaving(false);
      }
    }, 1500);
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, [votos, isLoadingRascunho, user?._id, perfil, isReadOnly, vpDestino]);

  // ── Auto-submit paralelo para limite === 1 (usuários comuns) ────────────────
  useEffect(() => {
    if (isLoadingRascunho || !user?._id || limite !== 1) return;

    if (!autoSubmitInitializedRef.current) {
      autoSubmitInitializedRef.current = true;
      lastAutoSubmitSnapshotRef.current = votos;
      return;
    }

    const prev = lastAutoSubmitSnapshotRef.current;
    const categoriasMudadas = new Set<string>();
    const keys = new Set([...Object.keys(prev), ...Object.keys(votos)]);
    keys.forEach((cat) => {
      const novoIds = (votos[cat]?.ids ?? []).join(',');
      const antigoIds = (prev[cat]?.ids ?? []).join(',');
      if (novoIds !== antigoIds) categoriasMudadas.add(cat);
    });
    if (categoriasMudadas.size === 0) return;
    lastAutoSubmitSnapshotRef.current = votos;

    categoriasMudadas.forEach((categoria) => {
      if (autoSubmitTimersRef.current[categoria]) {
        clearTimeout(autoSubmitTimersRef.current[categoria]);
      }
      autoSubmitTimersRef.current[categoria] = setTimeout(async () => {
        const atual = votosRef.current[categoria] ?? { ids: [], titulos: [] };
        try {
          await submitSelecaoEtapa3({
            userId: user._id,
            categoria,
            iniciativaIds: atual.ids,
            iniciativaTitulos: atual.titulos,
            limite,
            enviadoEm: Date.now(),
          });
        } catch (err) {
          console.error('[useVotacaoEtapa3] erro no auto-submit', err);
        } finally {
          delete autoSubmitTimersRef.current[categoria];
        }
      }, 500);
    });
  }, [votos, isLoadingRascunho, user?._id, limite]);

  // ── Flush no unload ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (!user?._id) return;
    const flush = () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);

      if (perfil === 'assessor') {
        if (!isReadOnly) {
          salvarPreliminarRascunho(
            {
              assessorId: user._id,
              assessorNome: user.name,
              vpMatricula: vpDestino?.matricula ?? '',
              vpNome: vpDestino?.nome ?? '',
              notasRascunho: votosRef.current,
              status: 'rascunho',
              atualizadoEm: Date.now(),
            },
            isPreliminarElbb
          ).catch(() => {});
        }
        return;
      }

      salvarRascunho(user._id, votosRef.current);

      if (limite === 1) {
        Object.entries(autoSubmitTimersRef.current).forEach(([categoria, timer]) => {
          clearTimeout(timer);
          const atual = votosRef.current[categoria] ?? { ids: [], titulos: [] };
          submitSelecaoEtapa3({
            userId: user._id,
            categoria,
            iniciativaIds: atual.ids,
            iniciativaTitulos: atual.titulos,
            limite,
            enviadoEm: Date.now(),
          }).catch(() => {});
        });
        autoSubmitTimersRef.current = {};
      }
    };
    window.addEventListener('beforeunload', flush);
    return () => window.removeEventListener('beforeunload', flush);
  }, [user?._id, perfil, limite, isReadOnly, vpDestino]);

  const handleToggleVoto = useCallback(
    (iniciativaId: string, titulo: string, categoriaId: string) => {
      if (isReadOnly) return;
      setVotos((prev) => {
        const atual = prev[categoriaId] ?? { ids: [], titulos: [] };
        const idx = atual.ids.indexOf(iniciativaId);
        if (idx !== -1) {
          return {
            ...prev,
            [categoriaId]: {
              ids: atual.ids.filter((_, i) => i !== idx),
              titulos: atual.titulos.filter((_, i) => i !== idx),
            },
          };
        }
        if (atual.ids.length >= limite) return prev;
        return {
          ...prev,
          [categoriaId]: {
            ids: [...atual.ids, iniciativaId],
            titulos: [...atual.titulos, titulo],
          },
        };
      });
    },
    [limite, isReadOnly]
  );

  const totalVotos = useMemo(
    () => Object.values(votos).reduce((acc, v) => acc + (v?.ids?.length ?? 0), 0),
    [votos]
  );

  const totalEsperado = CATEGORIAS_PARTICIPANTES_ETAPA3.length * limite;
  const prontoParaEnvio = totalVotos === totalEsperado;
  const podeEnviar = totalVotos > 0;

  const handleEnviarVotos = useCallback(async () => {
    if (perfil === 'assessor') return; // assessores usam handleEnviarParaAprovacao
    const canSubmit = limite === 1 ? podeEnviar : prontoParaEnvio;
    if (!user?._id || !canSubmit) return;
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await Promise.all(
        Object.entries(votos).map(([categoria, { ids, titulos }]) =>
          submitSelecaoEtapa3({
            userId: user._id,
            categoria,
            iniciativaIds: ids,
            iniciativaTitulos: titulos,
            limite,
            enviadoEm: Date.now(),
          })
        )
      );
      await deletarRascunhoAtivo(user._id);
      if (perfil === 'vp') {
        await marcarPreliminarSuperseded(user._id, isPreliminarElbb).catch(() => {});
      }
      try {
        await registrarVotar(user._id);
      } catch (actionLogError) {
        console.warn('Falha ao registrar action_log votar (Etapa 3):', actionLogError);
      }
      navigate('/confirmacao-voto-etapa-3');
    } catch (err) {
      console.error('[useVotacaoEtapa3] erro ao enviar votos', err);
      setSubmitError('Ocorreu um erro ao enviar seus votos. Tente novamente.');
    } finally {
      setIsSubmitting(false);
    }
  }, [user?._id, votos, prontoParaEnvio, podeEnviar, limite, perfil, navigate]);

  const handleEnviarParaAprovacao = useCallback(async () => {
    if (!user?._id || perfil !== 'assessor' || !prontoParaEnvio || isReadOnly) {
      return;
    }
    setIsSubmitting(true);
    setSubmitError(null);
    try {
      await enviarPreliminarParaAprovacao(
        {
          assessorId: user._id,
          assessorNome: user.name,
          vpMatricula: vpDestino?.matricula ?? '',
          vpNome: vpDestino?.nome ?? '',
          notasRascunho: votosRef.current,
          status: 'em_aprovacao',
          atualizadoEm: Date.now(),
          enviadoEm: Date.now(),
        },
        isPreliminarElbb
      );
      try {
        await registrarVotar(user._id);
      } catch (actionLogError) {
        console.warn('Falha ao registrar action_log votar (Etapa 3 — preliminar):', actionLogError);
      }
      navigate('/confirmacao-voto-etapa-3');
    } catch (err) {
      console.error('[useVotacaoEtapa3] erro ao enviar voto preliminar', err);
      setSubmitError(
        'Não foi possível enviar o voto preliminar. O VP pode já ter enviado o voto final.'
      );
    } finally {
      setIsSubmitting(false);
    }
  }, [user?._id, user?.name, perfil, prontoParaEnvio, isReadOnly, vpDestino, navigate]);

  const aceitarPreliminar = useCallback(() => {
    if (preliminarVpNotasRef.current) {
      setVotos(preliminarVpNotasRef.current);
    }
    setMostrarModalPreliminar(false);
  }, []);

  const recusarPreliminar = useCallback(() => {
    if (user?._id) {
      sessionStorage.setItem(PRELIMINAR_DISMISS_KEY, user._id);
    }
    setMostrarModalPreliminar(false);
  }, [user?._id]);

  return {
    votos,
    limite,
    perfil,
    vpDestino,
    isReadOnly,
    statusPreliminar,
    preliminarSuperseded,
    preliminarBaseadoNoVp,
    preliminarVp,
    mostrarModalPreliminar,
    isLoadingRascunho,
    isSaving,
    isSubmitting,
    submitError,
    handleToggleVoto,
    handleEnviarVotos,
    handleEnviarParaAprovacao,
    aceitarPreliminar,
    recusarPreliminar,
    prontoParaEnvio,
    podeEnviar,
  };
}
